(() => {
class ContactHub extends HTMLElement {
  constructor() {
    super();
    this.handleHistoryChange = this.handleHistoryChange.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.classList.add('is-enhanced');
    this.directory = this.querySelector('[data-contact-directory]');
    this.formPanel = this.querySelector('[data-contact-form-panel]');
    this.formTitle = this.querySelector('[data-contact-form-title]');
    this.formDescription = this.querySelector('[data-contact-form-description]');
    this.formState = this.querySelector('[data-contact-form-state]');
    this.topicSelect = this.querySelector('[data-contact-topic-select]');
    this.topicButtons = Array.from(this.querySelectorAll('[data-contact-topic]')).filter(
      (element) => element.tagName === 'BUTTON'
    );
    this.conditionalFields = Array.from(this.querySelectorAll('[data-contact-conditional]'));
    this.message = this.querySelector('[data-contact-message]');
    this.messageLabel = this.querySelector('[data-contact-message-label]');
    this.backButton = this.querySelector('[data-contact-back]');
    this.contactForm = this.querySelector('form');
    this.storageKey = `contact-topic-${this.dataset.sectionId || 'default'}`;
    const state = this.formState?.dataset.state || 'idle';

    if (!this.directory || !this.formPanel) return;

    this.cleanReturnParameter();

    if (state === 'success') {
      this.directory.hidden = true;
      this.formPanel.hidden = false;
      window.requestAnimationFrame(() => {
        this.querySelector('[role="status"]')?.focus({ preventScroll: true });
      });
      return;
    }

    if (!this.formTitle) return;

    this.topicButtons.forEach((button) => {
      button.addEventListener('click', () => this.openTopic(button.dataset.contactTopic, true, true));
    });

    this.backButton?.addEventListener('click', () => this.showDirectory(true, true));
    this.contactForm?.addEventListener('submit', () => this.storeTopic(this.activeTopic));
    window.addEventListener('popstate', this.handleHistoryChange);

    const hashTopic = this.getTopicFromHash();
    const storedTopic = this.getStoredTopic();
    const defaultTopic = this.topicButtons[0]?.dataset.contactTopic;

    if (window.location.hash === '#contact-options') {
      this.showDirectory(false, false);
    } else if (this.isValidTopic(hashTopic)) {
      this.openTopic(hashTopic, false, false);
    } else if (state !== 'idle' || window.location.hash === '#ContactForm') {
      this.openTopic(this.isValidTopic(storedTopic) ? storedTopic : defaultTopic, false, false);
    } else {
      this.configureTopic(defaultTopic);
      this.showDirectory(false, false);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handleHistoryChange);
  }

  isValidTopic(topic) {
    return Boolean(topic && this.topicButtons.some((button) => button.dataset.contactTopic === topic));
  }

  getTopicButton(topic) {
    return this.topicButtons.find((button) => button.dataset.contactTopic === topic);
  }

  configureTopic(topic) {
    const button = this.getTopicButton(topic);
    if (!button) return;

    this.activeTopic = topic;
    this.formTitle.textContent = button.dataset.formTitle || '';

    const description = button.dataset.formDescription || '';
    this.formDescription.textContent = description;
    this.formDescription.hidden = description.length === 0;

    if (this.messageLabel) this.messageLabel.textContent = button.dataset.messageLabel || 'Messaggio';
    if (this.message) this.message.placeholder = button.dataset.messagePlaceholder || 'Messaggio';

    if (this.topicSelect) {
      const subjectOption = Array.from(this.topicSelect.options).find(
        (option) => option.dataset.contactTopic === topic
      );
      if (subjectOption) this.topicSelect.value = subjectOption.value;
    }

    this.conditionalFields.forEach((field) => {
      const topics = (field.dataset.contactTopics || '').split(/\s+/).filter(Boolean);
      const isVisible = topics.includes(topic);
      field.hidden = !isVisible;

      field.querySelectorAll('input, select, textarea').forEach((control) => {
        control.disabled = !isVisible;
        const requiredTopics = (control.dataset.requiredTopics || '').split(/\s+/).filter(Boolean);
        const isRequired = isVisible && requiredTopics.includes(topic);
        control.required = isRequired;

        if (isRequired) {
          control.setAttribute('aria-required', 'true');
        } else {
          control.removeAttribute('aria-required');
        }
      });
    });

    this.topicButtons.forEach((topicButton) => {
      topicButton.setAttribute('aria-expanded', String(topicButton === button));
    });

    this.storeTopic(topic);
  }

  openTopic(topic, updateHistory, moveFocus) {
    if (!this.isValidTopic(topic)) return;

    this.configureTopic(topic);
    this.directory.hidden = true;
    this.formPanel.hidden = false;

    if (updateHistory) history.pushState({ contactTopic: topic }, '', `#contact-form-${topic}`);

    if (moveFocus) {
      window.requestAnimationFrame(() => {
        this.formPanel.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        this.formTitle.focus({ preventScroll: true });
      });
    }
  }

  showDirectory(updateHistory, moveFocus) {
    const activeButton = this.getTopicButton(this.activeTopic);
    const focusTarget = activeButton || this.topicButtons[0];
    this.formPanel.hidden = true;
    this.directory.hidden = false;
    this.topicButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));

    if (updateHistory) history.pushState({}, '', '#contact-options');

    if (moveFocus) {
      window.requestAnimationFrame(() => {
        this.directory.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        focusTarget?.focus({ preventScroll: true });
      });
    }
  }

  handleHistoryChange() {
    const topic = this.getTopicFromHash();

    if (this.isValidTopic(topic)) {
      this.openTopic(topic, false, true);
    } else if (window.location.hash === '#ContactForm') {
      const storedTopic = this.getStoredTopic();
      const defaultTopic = this.topicButtons[0]?.dataset.contactTopic;
      this.openTopic(this.isValidTopic(storedTopic) ? storedTopic : defaultTopic, false, true);
    } else {
      this.showDirectory(false, true);
    }
  }

  getTopicFromHash() {
    const prefix = '#contact-form-';
    if (!window.location.hash.startsWith(prefix)) return null;
    return decodeURIComponent(window.location.hash.slice(prefix.length));
  }

  cleanReturnParameter() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('contact_return')) return;

      url.searchParams.delete('contact_return');
      history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (_error) {
      // The temporary parameter is harmless if browser history is unavailable.
    }
  }

  storeTopic(topic) {
    if (!this.isValidTopic(topic)) return;

    try {
      window.sessionStorage.setItem(this.storageKey, topic);
    } catch (_error) {
      // sessionStorage can be unavailable in strict privacy modes.
    }
  }

  getStoredTopic() {
    try {
      return window.sessionStorage.getItem(this.storageKey);
    } catch (_error) {
      return null;
    }
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('contact-hub')) {
  customElements.define('contact-hub', ContactHub);
}
})();
